import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Avatar, 
  Rating, 
  keyframes,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { exploreApi } from '../../services/api';
import { CafeReview } from '../../types/explore';
import { SentimentDissatisfied } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { CardGridSkeleton } from '../skeletons';

interface TestimonialsSectionProps {
  isDark: boolean;
}

interface UserReview extends CafeReview {
  userAvatar?: string;
  userRole?: string;
  bgColor?: string;
}

const TestimonialSection: React.FC<TestimonialsSectionProps> = ({ isDark }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate(); // Initialize navigate
  
  const [isPaused, setIsPaused] = useState(false);
  const [reviews, setReviews] = useState<UserReview[]>([]);
  const [loading, setLoading] = useState(true);
  const testimonialsRef = useRef<HTMLDivElement>(null);

  // Styles & Data - Keeping your original styles
  const lightModeTextGradient = 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)';
  const secondaryText = isDark ? '#888888' : '#334D45';
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  // Responsive card styles
  const activeCardStyle = isDark ? {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(196, 182, 111, 0.2)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
    borderRadius: isMobile ? '16px' : isTablet ? '20px' : '24px',
  } : {
    background: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(12, 59, 46, 0.1)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    borderRadius: isMobile ? '16px' : isTablet ? '20px' : '24px',
  };

  const cardTitleColor = isDark ? 'white' : '#051814';
  const cardTextColor = isDark ? 'rgba(255,255,255,0.7)' : '#1D5C4A';
  const cafeNameColor = isDark ? '#C4B66F' : '#0F3B2E';

  // Get initials for avatar (fallback)
  const getInitials = (name: string): string => {
    if (!name) return 'UU';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Get avatar color based on user ID or name
  const getAvatarColor = (userId: string, index: number): string => {
    const colors = [
      '#1D5C4A',
      '#0F3B2E',
      '#051814',
      '#C4B66F',
      '#44A08D',
      '#2D7A67',
      '#1A4D3E',
    ];
    
    if (userId) {
      let hash = 0;
      for (let i = 0; i < userId.length; i++) {
        hash = userId.charCodeAt(i) + ((hash << 5) - hash);
      }
      const colorIndex = Math.abs(hash) % colors.length;
      return colors[colorIndex];
    }
    
    return colors[index % colors.length];
  };

  // Function to handle user profile click
  const handleUserProfileClick = (username: string) => {
    if (username) {
      navigate(`/${username}`);
    }
  };

  // Fetch real reviews from backend
  const fetchReviews = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await exploreApi.getRecentReviews();
      if (response.success && response.reviews && response.reviews.length > 0) {
        const reviewsWithUserData = response.reviews.map((review, index) => ({
          ...review,
          userName: review.userName || `User ${review.userId?.substring(0, 6)}`,
          userAvatar: review.userProfilePicture || getInitials(review.userName || `User ${review.userId?.substring(0, 6)}`),
          userRole: review.userRole || 'Remote Worker',
          bgColor: getAvatarColor(review.userId, index)
        }));
        setReviews(reviewsWithUserData);
      } else {
        setReviews([]);
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if string is a valid URL for profile picture
  const isValidProfilePicture = (url: string | undefined): boolean => {
    if (!url || url.trim() === '') return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Animation for scrolling - infinite loop
  const getCardWidth = () => {
    if (isMobile) return 280;
    if (isTablet) return 300;
    return 320;
  };

  const scrollAnimation = keyframes`
    0% { transform: translateX(0); }
    100% { transform: translateX(calc(-${getCardWidth()}px * ${Math.max(reviews.length, 5)})); }
  `;

  // Initial fetch
  useEffect(() => {
    fetchReviews();
  }, []);

  const animationDuration = Math.max(reviews.length * 5, 25);

  return (
    <Box sx={{ 
      py: { xs: 6, md: 12, lg: 16 },
      bgcolor: 'transparent',
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        background: isDark 
          ? 'linear-gradient(227.21deg, rgba(167, 167, 167, 0.05) -5.83%, rgba(21, 21, 21, 0.4) 106.44%)'
          : 'linear-gradient(227.21deg, rgba(255, 255, 255, 0.4) -5.83%, rgba(240, 240, 240, 0.8) 106.44%)',
        zIndex: 0,
      }
    }}>
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Responsive header text */}
        <Box sx={{ mb: { xs: 6, md: 8, lg: 10 }, textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem', lg: '4rem' },
              mb: { xs: 1.5, md: 2 },
              ...discordFont,
              background: isDark ? 'none' : lightModeTextGradient,
              WebkitBackgroundClip: isDark ? 'none' : 'text',
              WebkitTextFillColor: isDark ? 'white' : 'transparent',
              lineHeight: 1.2,
            }}
          >
            Community Stories
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: secondaryText,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem', lg: '1.25rem' },
              px: { xs: 2, sm: 0 },
            }}
          >
            Join thousands of remote workers finding community and collaboration
          </Typography>
        </Box>

        {loading ? (
          <CardGridSkeleton count={3} size={{ xs: 12, sm: 6, md: 4 }} label="Loading testimonials" />
        ) : reviews.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: { xs: 200, md: 300 },
            textAlign: 'center',
            px: { xs: 2, md: 0 }
          }}>
            <SentimentDissatisfied sx={{ 
              fontSize: { xs: 48, md: 60 }, 
              color: '#C4B66F', 
              mb: 2,
              opacity: 0.7
            }} />
            <Typography
              variant="h5"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                mb: 1,
                fontWeight: 600,
                fontSize: { xs: '1.25rem', md: '1.5rem' }
              }}
            >
              No Feedback from users found
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                maxWidth: { xs: 300, md: 400 },
                fontSize: { xs: '0.9rem', md: '1rem' }
              }}
            >
              Be the first to share your experience and help others discover great cafes
            </Typography>
          </Box>
        ) : (
          <Box
            ref={testimonialsRef}
            sx={{
              display: 'flex',
              gap: { xs: 2, sm: 3, md: 4 },
              animation: `${scrollAnimation} ${animationDuration}s linear infinite`,
              animationPlayState: isPaused ? 'paused' : 'running',
              width: `calc(${getCardWidth()}px * ${reviews.length * 2})`,
              '&:hover': {
                animationPlayState: 'paused',
              },
              paddingBottom: { xs: 1, md: 0 },
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {[...reviews, ...reviews].map((review, index) => {
              const hasProfilePicture = isValidProfilePicture(review.userProfilePicture);
              const username = review.userName || ''; // Get username or empty string
              
              return (
                <Box
                  key={`${review.id}-${index}`}
                  sx={{
                    flex: `0 0 ${getCardWidth() - 40}px`,
                    p: { xs: 2.5, sm: 3, md: 3.5, lg: 4 },
                    ...activeCardStyle,
                    transition: 'all 0.3s ease',
                    minHeight: { xs: '280px', sm: '300px', md: '320px', lg: '340px' },
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: isDark 
                        ? '0 15px 30px rgba(12, 59, 46, 0.3)' 
                        : '0 15px 30px rgba(0,0,0,0.1)',
                      borderColor: isDark ? '#C4B66F' : '#0F3B2E',
                    },
                  }}
                >
                  <Rating 
                    value={review.rating} 
                    readOnly 
                    sx={{ 
                      mb: { xs: 1.5, md: 2 }, 
                      color: '#C4B66F',
                      fontSize: { xs: '1.25rem', md: '1.5rem' }
                    }} 
                  />
                  
                  <Typography
                    sx={{
                      fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                      mb: { xs: 1.5, md: 2 },
                      color: cafeNameColor,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontStyle: 'normal',
                    }}
                  >
                    {review.cafeName || 'Cafe'}
                  </Typography>
                  
                  <Typography
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem', lg: '1.05rem' },
                      mb: { xs: 2.5, md: 3, lg: 4 },
                      color: cardTitleColor,
                      lineHeight: 1.6,
                      fontStyle: 'italic',
                      minHeight: { xs: '80px', sm: '90px', md: '100px' },
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    "{review.feedback || `Had a great experience at ${review.cafeName}`}"
                  </Typography>
                  
                  {/* User profile section - Made clickable */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 1.5, md: 2 },
                      mt: 'auto',
                      cursor: username ? 'pointer' : 'default', // Show pointer cursor if username exists
                      '&:hover': username ? {
                        '& .user-avatar': {
                          transform: 'scale(1.05)',
                          transition: 'transform 0.2s ease',
                        },
                        '& .user-name': {
                          color: isDark ? '#C4B66F' : '#0F3B2E',
                        }
                      } : {},
                    }}
                    onClick={() => username && handleUserProfileClick(username)}
                  >
                    {/* Avatar with click handler */}
                    <Box 
                      className="user-avatar"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event bubbling
                        username && handleUserProfileClick(username);
                      }}
                      sx={{ cursor: username ? 'pointer' : 'default' }}
                    >
                      {hasProfilePicture ? (
                        <Avatar
                          src={review.userProfilePicture}
                          sx={{
                            bgcolor: review.bgColor,
                            color: '#C4B66F',
                            width: { xs: 40, md: 48, lg: 52 },
                            height: { xs: 40, md: 48, lg: 52 },
                            fontWeight: 700,
                            border: '2px solid rgba(255,255,255,0.1)',
                            fontSize: { xs: '0.9rem', md: '1rem' }
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            bgcolor: review.bgColor,
                            color: '#C4B66F',
                            width: { xs: 40, md: 48, lg: 52 },
                            height: { xs: 40, md: 48, lg: 52 },
                            fontWeight: 700,
                            border: '2px solid rgba(255,255,255,0.1)',
                            fontSize: { xs: '0.9rem', md: '1rem' }
                          }}
                        >
                          {review.userAvatar || getInitials(review.userName || 'User')}
                        </Avatar>
                      )}
                    </Box>
                    
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography 
                        className="user-name"
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem', lg: '1.05rem' },
                          ...discordFont,
                          color: cardTitleColor,
                          fontWeight: 600,
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {review.userName || 'Anonymous User'}
                      </Typography>
                      <Typography sx={{ 
                        color: cardTextColor, 
                        fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem', lg: '0.95rem' },
                        lineHeight: 1.3,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {review.userRole || 'Remote Worker'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default TestimonialSection;